import { zodResolver } from "@hookform/resolvers/zod";
import { PaymentGateway } from "@prisma/client";
import React, { useState, useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";

import { trpc } from "@calcom/trpc/react";
import { Button, Label, Select, TextAreaField, TextField, Form, showToast, Switch } from "@calcom/ui";

import { calculateFee } from "@lib/finix";
import { canSelectPmsPaymentType, getDefaultPmsPaymentTypeId } from "@lib/payment/nexhealthPaymentType";
import { formatAmountToTwoDecimals } from "@lib/practice/helper";

import { DollarIcon } from "@components/Icons";
import { AlertIcon } from "@components/Icons";
import { useOfficeContext } from "@components/navbar";
import NexhealthPaymentTypeSelect from "@components/patients/NexhealthPaymentTypeSelect";
import { OfficePatientSelect } from "@components/patients/OfficePatientSelect";
import PaymentSuccessDialog from "@components/patients/PaymentSuccessDialog";
import FamilyPaymentAllocation from "@components/patients/terminalPayment/FamilyPaymentAllocation";
import type { Allocation } from "@components/patients/terminalPayment/FamilyPaymentAllocation";

type Props = {
  patientId?: number;
  officeId: number;
  setPaymentDialog: React.Dispatch<React.SetStateAction<boolean>>;
};

type FormValues = {
  amount: string;
  note: string;
  terminal: string;
  discountAmount: number;
  discountPercentage: number;
  printReceipt: boolean;
  pmsPaymentTypeId?: string;
};

export default function TerminalPayment({ patientId, officeId, setPaymentDialog }: Props) {
  const [loading, setLoading] = useState(false);
  const [feeCharged, setFeeCharged] = useState(false);
  const [processedAmount, setProcessedAmount] = useState(0);
  const [patient, setPatient] = useState<number | undefined>(patientId);
  const [feeAmount, setFeeAmount] = useState(0);
  const [splitEnabled, setSplitEnabled] = useState(false);
  const [allocations, setAllocations] = useState<Allocation[]>([]);

  const [paymentSuccessDialogOpen, setPaymentSuccessDialogOpen] = useState(false);

  const { data, isLoading: terminalLoading } = trpc.viewer.terminal.list.useQuery({ officeId });
  const { data: userDefaultDeviceId } = trpc.viewer.terminal.getUserDefault.useQuery({ officeId });
  const { office } = useOfficeContext();
  const showPaymentTypeSelect = canSelectPmsPaymentType(office);
  const defaultPaymentTypeId = getDefaultPmsPaymentTypeId({ office, isTerminal: true });

  const terminalList = data
    ? data.map((d) => ({ label: d.name + ` - Nickname: ${d.nickname || ""}`, value: d.deviceId }))
    : [];

  const form = useForm<FormValues>({
    defaultValues: {
      amount: "0.00",
      discountAmount: 0,
      discountPercentage: 0,
      printReceipt: false,
    },
    resolver: zodResolver(
      z.object({
        terminal: z.string().min(1, "Required"),
        amount: z
          .string()
          .regex(/^\d+\.\d{2}$/, { message: "Must have exactly 2 decimal places" })
          .refine((val) => parseFloat(val) > 0, { message: "Should be greater than 0" }),
        note: z.string().optional(),
        discountAmount: z.number().optional(),
        discountPercentage: z.number().optional(),
        printReceipt: z.boolean(),
        pmsPaymentTypeId: z.string().optional(),
      })
    ),
  });
  const { register, formState, control, setValue, reset, watch } = form;
  const watchAmount = watch("amount");
  const enableSurcharge = !!(office && (office.applySurchargeFee || office.enableDualPricing));

  useEffect(() => {
    if (enableSurcharge && office && office.paymentGateway === PaymentGateway.FINIX) {
      const fee = calculateFee({
        amount: Math.round(parseFloat(watchAmount) * 100),
        isTerminal: true,
        applySurcharge: enableSurcharge,
        finixFeeProfile: office?.FinixMerchantFeeProfile || undefined,
        isACHPayment: false,
        isCreditCardPayment: true,
      });
      setFeeAmount(fee / 100);
    } else {
      setFeeAmount(0);
    }
  }, [enableSurcharge, watchAmount, office]);

  useEffect(() => {
    if (data && data.length > 0) {
      const userDefault =
        userDefaultDeviceId && data.some((d) => d.deviceId === userDefaultDeviceId)
          ? data.find((d) => d.deviceId === userDefaultDeviceId)
          : null;
      const terminal = userDefault || data.find((d) => d.default) || data[0];
      setValue("terminal", terminal.deviceId);
    }
  }, [data, userDefaultDeviceId]);

  useEffect(() => {
    if (showPaymentTypeSelect && defaultPaymentTypeId) {
      setValue("pmsPaymentTypeId", defaultPaymentTypeId);
    }
  }, [showPaymentTypeSelect, defaultPaymentTypeId, setValue]);

  const { data: singlePatient } = trpc.viewer.patients.getById.useQuery(
    {
      billingOfficeId: officeId,
      patientId: Number(patient),
      _tag: "terminalPayment",
    },
    { enabled: !!patient, refetchInterval: false, refetchOnReconnect: false, refetchOnWindowFocus: false }
  );

  const { data: familyData } = trpc.viewer.patients.getFamily.useQuery(
    {
      patientId: Number(patient),
      officeId,
    },
    { enabled: !!patient, refetchInterval: false, refetchOnReconnect: false, refetchOnWindowFocus: false }
  );

  const familyMembers = useMemo(() => {
    if (!patient || !familyData?.members?.length) return [];
    // Avoid flashing the previous family's members while the new query loads
    if (!familyData.members.some((m) => m.patientId === patient)) return [];
    return familyData.members.map((member) => ({
      patientId: member.patientId,
      name: member.name,
      type: member.type,
      balance: member.balance,
    }));
  }, [familyData, patient]);

  useEffect(() => {
    if (singlePatient) {
      setValue("amount", formatAmountToTwoDecimals(singlePatient.amountDue));
    }
  }, [singlePatient]);

  useEffect(() => {
    setSplitEnabled(false);
    setAllocations([]);
  }, [patient]);

  useEffect(() => {
    if (office?.enableFamilyAllocation && familyMembers.length > 1) {
      setSplitEnabled(true);
    } else {
      setSplitEnabled(false);
      setAllocations([]);
    }
  }, [familyMembers, office?.enableFamilyAllocation]);

  const sendToTerminal = trpc.viewer.patients.processTerminalPayment.useMutation({
    onSuccess: ({ patient, amount, feeCharged }) => {
      setProcessedAmount(amount);
      setFeeCharged(feeCharged);
      showToast("Successfully Paid", "success");
      setLoading(false);
      setPaymentSuccessDialogOpen(true);
    },
    onError: (err) => {
      showToast(err.message, "error");
      setLoading(false);
    },
  });

  const allocationsBalanced = useMemo(() => {
    if (!splitEnabled || familyMembers.length <= 1) return true;
    const totalCents = Math.round((parseFloat(watchAmount) || 0) * 100);
    const allocatedCents = allocations.reduce((sum, a) => sum + Math.round(a.amount * 100), 0);
    return totalCents === allocatedCents && allocations.length > 0;
  }, [splitEnabled, familyMembers.length, watchAmount, allocations]);

  const handleSubmit = (data: FormValues) => {
    if (!patient) {
      showToast("Please select a patient", "error");
      return;
    }
    if (splitEnabled && !allocationsBalanced) {
      showToast("Family allocation must equal the payment amount", "error");
      return;
    }
    setLoading(true);
    sendToTerminal.mutate({
      patientId: patient,
      officeId,
      amount: +data.amount,
      deviceId: data.terminal,
      note: data.note,
      default: true,
      printReceipt: data.printReceipt,
      overridePaymentTypeId: showPaymentTypeSelect ? data.pmsPaymentTypeId : undefined,
      allocations:
        splitEnabled && allocations.length > 0
          ? allocations.filter((a) => a.amount > 0).map((a) => ({ patientId: a.patientId, amount: a.amount }))
          : undefined,
    });
  };

  const resetFormState = () => {
    setPatient(undefined);
    setSplitEnabled(false);
    setAllocations([]);
    reset();
    if (showPaymentTypeSelect && defaultPaymentTypeId) {
      setValue("pmsPaymentTypeId", defaultPaymentTypeId);
    }
  };

  return (
    <div className="rounded-md bg-white text-sm text-gray-500">
      <Form form={form} handleSubmit={handleSubmit}>
        <div className="space-y-3">
          <div className="mt-6 flex flex-col gap-5">
            <div id="terminal-payment-account-select">
              <Label>Account</Label>
              <OfficePatientSelect
                officeId={officeId}
                value={patient}
                onChange={setPatient}
                placeholder="Search Account"
                className="rounded-xl"
                showMissingPatientHelp
              />
            </div>
            <div id="terminal-payment-terminal-select">
              <Label>Card Terminal</Label>
              <Controller
                name="terminal"
                render={({ field: { value, onChange } }) => (
                  <>
                    <Select
                      className="w-full"
                      instanceId="terminalid"
                      placeholder="Select Terminal"
                      options={terminalList}
                      value={terminalList.find((p) => p.value === value)}
                      onChange={(option) => option && onChange(option.value)}
                      isLoading={terminalLoading}
                    />
                  </>
                )}
              />
              {formState.errors.terminal && (
                <div className="text-gray mt-2 flex items-center gap-x-2 text-sm text-red-700">
                  <div>
                    <AlertIcon />
                  </div>
                  <p>Required</p>
                </div>
              )}
            </div>
            {showPaymentTypeSelect && (
              <div id="terminal-payment-type-select">
                <NexhealthPaymentTypeSelect
                  officeId={officeId}
                  name="pmsPaymentTypeId"
                  control={control}
                  instanceId="terminal-nexhealth-payment-type"
                />
              </div>
            )}
            <div id="terminal-payment-amount-input">
              <Label>Amount</Label>
              <Controller
                name="amount"
                control={control}
                render={({ field: { value, onChange, onBlur, ...field } }) => (
                  <TextField
                    className="relative rounded-lg text-gray-900"
                    label=""
                    addOnLeading={<DollarIcon />}
                    {...field}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={() => {
                      const formatted = formatAmountToTwoDecimals(value);
                      if (formatted !== value) onChange(formatted);
                      onBlur();
                    }}
                  />
                )}
              />
            </div>
            {office?.enableFamilyAllocation && familyMembers.length > 1 && (
              <FamilyPaymentAllocation
                members={familyMembers}
                totalAmount={watchAmount}
                enabled={splitEnabled}
                onEnabledChange={setSplitEnabled}
                allocations={allocations}
                onAllocationsChange={setAllocations}
              />
            )}
            <div id="terminal-payment-note-textarea" className="md:col-span-1">
              <Label>Note</Label>
              <TextAreaField
                className="rounded-lg text-gray-900"
                placeholder="Note will be included with transaction details."
                rows={3}
                {...register("note")}
                label=""
              />
            </div>
            {office?.paymentGateway === PaymentGateway.PAYRIX && (
              <div id="terminal-payment-print-receipt-switch" className="md:col-span-2">
                <Controller
                  name="printReceipt"
                  render={({ field: { value, onChange } }) => (
                    <Switch label="Print Receipt" checked={value} onCheckedChange={onChange} />
                  )}
                />
              </div>
            )}
            <Button
              id="terminal-payment-submit-button"
              className="flex h-10 justify-center bg-black text-white hover:bg-black hover:text-white"
              type="submit"
              loadingTextClassName="text-black"
              loading={loading}
              disabled={splitEnabled && !allocationsBalanced}>
              Send to{" "}
              {office?.paymentGateway === PaymentGateway.FINIX && enableSurcharge
                ? `$${watchAmount} + $${feeAmount.toFixed(2)}`
                : ""}{" "}
              Terminal
            </Button>
          </div>
        </div>
      </Form>
      {singlePatient && (
        <PaymentSuccessDialog
          title="Payment Sent to Terminal"
          description="Thanks for your payment, your payment was successful."
          amount={processedAmount}
          account={singlePatient.firstName + " " + singlePatient.lastName}
          date={new Date().toLocaleDateString()}
          source="Terminal Payment"
          open={paymentSuccessDialogOpen}
          handler={() => {
            setPaymentSuccessDialogOpen(false);
            resetFormState();
          }}
          onClose={() => {
            setPaymentSuccessDialogOpen(false);
            setPaymentDialog(false);
            resetFormState();
          }}
          email={singlePatient.email || ""}
          phone={singlePatient.phoneNumbers[0]?.number || ""}
          feeCharged={feeCharged}
        />
      )}
    </div>
  );
}
